import { SyntheticEvent } from 'react';
import { ZodSchema } from 'zod';
import {
  VisualComponentsContextProvider,
  VisualComponentsContextState,
  QueryStateContextProvider,
  GetQueryStateRefType,
} from './contexts';
import { QueryRenderer } from './renderers/query/QueryRenderer';
import { CompleteQuery, EditableQuery, ESIndexConfig } from './types';
import { QueryValidationContextProvider } from './contexts/queryValidation';
import { QueryErrorStateContextProvider } from './contexts/queryErrorState';
import { FieldMappingsContextProvider } from './contexts/fieldMappings';
import {
  QueryHistoryContextProvider,
  QueryHistoryStateContextProps,
} from './contexts/queryHistory';

type QueryBuilderFormProps = ESIndexConfig &
  QueryHistoryStateContextProps & {
    id: string;
    isLoading?: boolean;
    query?: CompleteQuery;
    querySchema?: ZodSchema<CompleteQuery>;
    onSetEditableQuery?: (query: EditableQuery) => void; // remove when yes/no and AI query summary tile is updated to not depend on this
    onSubmitSuccess: (
      query: CompleteQuery,
      event?: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    ) => void;
    onSubmitError?: () => void;
    isReadOnly?: boolean;
    /** allows for custom components in the query builder, uses defaults if none are provided */
    visualComponents?: Partial<VisualComponentsContextState>;
    /**
     * When a feature needs to set errors/warnings outside of QueryBuilder we need to set hasCustomValidationHandling to true.
     * This will remove the QueryErrorStateContextProvider within QueryBuilder.
     **/
    hasCustomValidationHandling?: boolean;
    /**
     * Ref that provides access to a function to get the current state values from QueryStateContextProvider.
     * This allows consumers to access the query builder state by calling the function through the ref.
     * The returned in the query state will be of type EditableQuery since it doesn't pass through validation.
     * It will only have the default query builder transformations in schema/utils/transformations applied.
     * 
     * Example implementation:
     *  const getQueryStateRef = useRef<any>(); 
     *  ...
     *  <Button
          onClick={() => {
            const queryState = getQueryStateRef?.current();
          }}
        >
     */
    getQueryState?: GetQueryStateRefType;
  };

export const QueryBuilderForm = ({
  visualComponents,
  query: passedQuery,
  querySchema,
  onSubmitSuccess,
  onSubmitError,
  id: formId,
  hasCustomValidationHandling,
  passedUndoStack,
  passedRedoStack,
  ...qbProps
}: QueryBuilderFormProps) => {
  return (
    <QueryStateContextProvider passedQuery={passedQuery} {...qbProps}>
      <FieldMappingsContextProvider>
        <QueryHistoryContextProvider
          passedUndoStack={passedUndoStack}
          passedRedoStack={passedRedoStack}
        >
          <QueryErrorStateContextProvider hasCustomValidationHandling={hasCustomValidationHandling}>
            <QueryValidationContextProvider
              onSubmitSuccess={onSubmitSuccess}
              onSubmitError={onSubmitError}
              schema={querySchema}
              formId={formId}
            >
              <VisualComponentsContextProvider {...visualComponents}>
                <QueryRenderer />
              </VisualComponentsContextProvider>
            </QueryValidationContextProvider>
          </QueryErrorStateContextProvider>
        </QueryHistoryContextProvider>
      </FieldMappingsContextProvider>
    </QueryStateContextProvider>
  );
};
